import { Card, CardContent, Checkbox, Stack, Typography } from '@mui/material';
import type { AccessCentrePermission } from 'domains/accessCentre/types';

const REQUIRES_PREFIX = 'Requires : ';

/** Display name from a permission key: part after '.' (e.g. "content.view" → "View"). */
const getDisplayNameFromKey = (key: string): string => {
    if (!key) return key;
    const afterDot = key.includes('.') ? key.split('.').slice(1).join('.') : key;
    const withSpaces = afterDot.replace(/_/g, ' ');
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1).toLowerCase();
};

const permissionCardStyles = {
    cursor: 'pointer',
    minHeight: 72,
    display: 'flex',
    alignItems: 'flex-start',
    minWidth: 0,
    overflow: 'hidden',
};

export type PermissionCardProps = {
    permission: AccessCentrePermission;
    label: string;
    isSelected: boolean;
    onToggle: (id: number) => void;
    readOnly?: boolean;
};

const PermissionCard = ({ permission, label, isSelected, onToggle, readOnly = false }: PermissionCardProps) => {
    const hasDependencies = permission.depends_on && permission.depends_on.length > 0;
    const requiresText = hasDependencies
        ? REQUIRES_PREFIX + permission.depends_on!.map(getDisplayNameFromKey).join(', ')
        : '';

    return (
        <Card
            variant="outlined"
            sx={{
                ...permissionCardStyles,
                ...(readOnly && { cursor: 'default', pointerEvents: 'none' }),
                borderColor: isSelected ? 'primary.main' : undefined,
                borderWidth: isSelected ? 2 : 1,
            }}
            onClick={readOnly ? undefined : () => onToggle(permission.id)}
            data-testid={`access-center-permission-option-${permission.id}`}
        >
            <CardContent sx={{ py: 1, width: '100%', minWidth: 0, '&:last-child': { pb: 1 } }}>
                <Stack spacing={-1} alignItems="flex-start" sx={{ minWidth: 0, width: '100%' }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, width: '100%' }}>
                        <Checkbox
                            size="small"
                            checked={isSelected}
                            disabled={readOnly}
                            onChange={() => onToggle(permission.id)}
                            onClick={(e) => e.stopPropagation()}
                            inputProps={{ 'aria-label': `Select ${label}` }}
                            data-testid={`access-center-permission-checkbox-${permission.id}`}
                        />
                        <Typography
                            variant="body2"
                            title={label}
                            sx={{
                                minWidth: 0,
                                flex: 1,
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                lineHeight: 1.2,
                            }}
                        >
                            {label}
                        </Typography>
                    </Stack>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            pl: '52px',
                            fontSize: '0.75rem',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            minWidth: 0,
                            paddingTop: 1,
                        }}
                        data-testid={`access-center-permission-requires-${permission.id}`}
                    >
                        {hasDependencies ? requiresText : ''}
                    </Typography>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default PermissionCard;
